import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { fmtMoney } from "@/lib/format";
import { toast } from "sonner";
import {
  ResponsiveContainer, ComposedChart, Bar, Line, LineChart, XAxis, YAxis, Tooltip, CartesianGrid, ReferenceLine, Legend,
} from "recharts";
import { Plus, Save, Trash2 } from "lucide-react";
import { Card, Eyebrow, Kpi, NumField, btnDark, monthLabel, MonthDateInput } from "./ui";
import ScenarioBar from "./ScenarioBar";
import { TrainingVideoButton } from "./TrainingVideo";
import WhatIfPlanner, { cleanItems } from "./WhatIfPlanner";

const axis = { fontSize: 11, fill: "#475569", fontFamily: "IBM Plex Mono" };
const tip = { background: "#fff", border: "1px solid #0F172A", borderRadius: 0, fontSize: 12, fontFamily: "IBM Plex Mono" };
const SCEN = { base: ["Base", "#0F172A"], best: ["Best", "#059669"], worst: ["Worst", "#DC2626"] };

export default function BurnTab({ ov, reload, remount }) {
  const b = ov.burn;
  const overdrawn = b.cash_now < 0;
  const runwayText = b.cash_flow_positive ? "Cash-flow positive" : overdrawn ? "Cash already negative" : b.runway_months != null ? `${b.runway_months} months` : "—";
  return (
    <div className="space-y-5">
      <div className="flex justify-end -mb-2"><TrainingVideoButton video="burn" testid="cash-burn-training-btn" /></div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi testid="burn-kpi-cash" label="Cash on hand" value={fmtMoney(b.cash_now)} tone={b.cash_now < 0 ? "bad" : undefined} sub="Checking + savings" />
        <Kpi testid="burn-kpi-net-burn" label="Avg net burn (3 mo)" value={fmtMoney(b.avg_net_burn["3"])}
          sub={`6 mo ${fmtMoney(b.avg_net_burn["6"])} · 12 mo ${fmtMoney(b.avg_net_burn["12"])}`} tone={b.avg_net_burn["3"] > 0 ? "bad" : "good"} />
        <Kpi testid="burn-kpi-runway" label="Runway" value={runwayText} tone={b.cash_flow_positive ? "good" : (b.runway_months ?? 0) < 6 ? "bad" : undefined}
          sub={b.cash_flow_positive ? "Inflows cover outflows" : overdrawn ? "Add cash or credit now" : b.runway_until ? `Cash runs out ~${monthLabel(b.runway_until)}` : ""} />
        <Kpi testid="burn-kpi-positive-months" label="Cash-positive months" value={`${b.positive_months} / ${b.months.length}`} sub="Months where inflow ≥ outflow" />
      </div>
      <Card testid="burn-history-card">
        <Eyebrow>Last {b.months.length} months</Eyebrow>
        <div className="font-display font-bold text-lg mb-4">Burn vs inflow & month-end cash</div>
        <div className="h-72" data-testid="burn-history-chart">
          <ResponsiveContainer>
            <ComposedChart data={b.months} margin={{ right: 28, left: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
              <XAxis dataKey="month" tick={axis} tickFormatter={monthLabel} />
              <YAxis tick={axis} />
              <Tooltip contentStyle={tip} formatter={(v) => fmtMoney(v)} labelFormatter={monthLabel} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="inflow" name="Inflow" fill="#059669" />
              <Bar dataKey="gross_burn" name="Gross burn" fill="#DC2626" />
              <Line dataKey="ending_cash" name="Month-end cash" stroke="#0F172A" strokeWidth={2} dot={false} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </Card>
      <ForecastCard ov={ov} reload={reload} remount={remount} />
    </div>
  );
}

function ForecastCard({ ov, reload, remount }) {
  const [p, setP] = useState({ plan_items: [], ...ov.settings.forecast });
  const [fc, setFc] = useState(ov.forecast);
  const [previewErr, setPreviewErr] = useState("");
  const timer = useRef(null);
  const errText = (err) => {
    const d = err?.response?.data?.detail;
    return Array.isArray(d) ? `${d[0]?.loc?.slice(-1)[0] || "value"}: ${d[0]?.msg}` : d || "Invalid assumptions";
  };
  const buildBody = (x) => ({
    revenue_growth_pct: Number(x.revenue_growth_pct) || 0,
    cost_change_pct: Number(x.cost_change_pct) || 0,
    cash_buffer: Number(x.cash_buffer) || 0,
    one_offs: (x.one_offs || []).filter((o) => /^\d{4}-\d{2}$/.test(o.month)).map((o) => ({ ...o, amount: Number(o.amount) || 0 })),
    plan_items: cleanItems(x.plan_items),
  });

  useEffect(() => {
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      try {
        setFc((await api.post("/advisory/forecast", buildBody(p))).data);
        setPreviewErr("");
      } catch (err) { setPreviewErr(errText(err)); }
    }, 400);
    return () => clearTimeout(timer.current);
  }, [p]);

  const save = async () => {
    try {
      const body = buildBody(p);
      const dropped = (p.plan_items || []).length - body.plan_items.length;
      await api.put("/advisory/settings", { forecast: body });
      if (dropped > 0) toast.warning(`${dropped} what-if item(s) without a valid start month were not saved`);
      toast.success("Forecast assumptions saved");
      reload();
    } catch (err) { toast.error(errText(err)); }
  };
  const setOne = (i, k, v) => setP((x) => ({ ...x, one_offs: x.one_offs.map((o, j) => (j === i ? { ...o, [k]: v } : o)) }));

  const chart = (fc.scenarios.base.months || []).map((m, i) => ({
    month: m.month, base: m.ending_cash, best: fc.scenarios.best.months[i].ending_cash, worst: fc.scenarios.worst.months[i].ending_cash,
    with_plan: fc.plan?.months?.[i]?.ending_cash,
  }));

  return (
    <Card testid="forecast-card">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <Eyebrow>12-month forecast</Eyebrow>
          <div className="font-display font-bold text-lg">Future cash & credit needs</div>
          <div className="text-xs text-[var(--ll-ink-soft)] mt-1">Starts from your 3-month average inflow & outflow. Best/Worst shift growth by +2/−3 pts and costs by −1/+2 pts per month.</div>
          {previewErr && <div className="text-xs mt-1 text-[var(--ll-error)]" data-testid="forecast-error">Not updated — {previewErr}</div>}
        </div>
        <button onClick={save} className={btnDark} data-testid="forecast-save-btn"><Save size={14} /> Save assumptions</button>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
        <div className="lg:col-span-4 space-y-3">
          <NumField label="Revenue growth / month" suffix="%" step="0.5" value={p.revenue_growth_pct} onChange={(v) => setP({ ...p, revenue_growth_pct: v })} testid="forecast-rev-growth" />
          {!ov.burn.avg_inflow_3 && (
            <div className="text-[11px] text-[var(--ll-ochre)] -mt-2" data-testid="forecast-no-inflow-hint">No inflow in the last 3 months — growth has nothing to grow. Add expected revenue as one-off items.</div>
          )}
          <NumField label="Cost change / month" suffix="%" step="0.5" value={p.cost_change_pct} onChange={(v) => setP({ ...p, cost_change_pct: v })} testid="forecast-cost-change" />
          <NumField label="Minimum cash buffer" suffix="$" step="500" min="0" value={p.cash_buffer} onChange={(v) => setP({ ...p, cash_buffer: v })} testid="forecast-buffer" />
          <div>
            <div className="flex items-center justify-between">
              <span className="text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)]">One-off items</span>
              <button
                onClick={() => setP({ ...p, one_offs: [...(p.one_offs || []), { month: chart[0]?.month || "", amount: -1000, label: "" }] })}
                className="text-xs inline-flex items-center gap-1 hover:underline" data-testid="forecast-add-oneoff"
              ><Plus size={12} /> Add</button>
            </div>
            {(p.one_offs || []).map((o, i) => (
              <div key={i} className="flex gap-1.5 mt-2" data-testid={`forecast-oneoff-${i}`}>
                <MonthDateInput value={o.month} onChange={(v) => setOne(i, "month", v)} edge="start" className="border px-2 py-1 text-xs w-[100px] font-mono-fin" style={{ borderColor: "var(--ll-border)" }} testid={`forecast-oneoff-month-${i}`} />
                <input type="number" value={o.amount} onChange={(e) => setOne(i, "amount", e.target.value)} className="border px-2 py-1 text-xs w-[90px] font-mono-fin" style={{ borderColor: "var(--ll-border)" }} data-testid={`forecast-oneoff-amount-${i}`} />
                <input value={o.label} placeholder="Label" onChange={(e) => setOne(i, "label", e.target.value)} className="border px-2 py-1 text-xs flex-1 min-w-0" style={{ borderColor: "var(--ll-border)" }} />
                <button onClick={() => setP({ ...p, one_offs: p.one_offs.filter((_, j) => j !== i) })} className="px-1.5 border" style={{ borderColor: "var(--ll-border)" }} data-testid={`forecast-oneoff-remove-${i}`}><Trash2 size={12} /></button>
              </div>
            ))}
            <div className="text-[11px] text-[var(--ll-ink-muted)] mt-1">Negative = expense (e.g. equipment), positive = inflow (e.g. loan, big contract).</div>
          </div>
        </div>
        <div className="lg:col-span-8">
          <div className="h-64" data-testid="forecast-chart">
            <ResponsiveContainer>
              <LineChart data={chart} margin={{ right: 28, left: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                <XAxis dataKey="month" tick={axis} tickFormatter={monthLabel} />
                <YAxis tick={axis} />
                <Tooltip contentStyle={tip} formatter={(v) => fmtMoney(v)} labelFormatter={monthLabel} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <ReferenceLine y={Number(fc.params.cash_buffer) || 0} stroke="#D97706" strokeDasharray="4 4" label={{ value: "Buffer", fontSize: 10, fill: "#B45309" }} />
                {Object.entries(SCEN).map(([k, [name, color]]) => (
                  <Line key={k} dataKey={k} name={name} stroke={color} strokeWidth={k === "base" ? 2.5 : 1.5} dot={false} />
                ))}
                {fc.plan && <Line dataKey="with_plan" name="Base + what-if plan" stroke="#7C3AED" strokeWidth={2.5} strokeDasharray="6 3" dot={false} />}
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4">
            {Object.entries(SCEN).map(([k, [name, color]]) => {
              const s = fc.scenarios[k];
              return (
                <div key={k} className="border p-3" style={{ borderColor: "var(--ll-border)", borderTop: `3px solid ${color}` }} data-testid={`forecast-scenario-${k}`}>
                  <div className="text-xs font-medium">{name} case</div>
                  <div className="text-[11px] text-[var(--ll-ink-soft)] mt-1">
                    {s.first_shortfall_month ? <>Below buffer by <b>{monthLabel(s.first_shortfall_month)}</b></> : "Stays above buffer"}
                  </div>
                  <div className="text-[11px] mt-1">Lowest cash <span className="font-mono-fin">{fmtMoney(s.min_cash)}</span> ({monthLabel(s.min_cash_month)})</div>
                  <div className="text-sm mt-2 font-mono-fin font-bold" style={{ color: s.credit_needed > 0 ? "var(--ll-error)" : "var(--ll-success)" }} data-testid={`forecast-credit-${k}`}>
                    {s.credit_needed > 0 ? `Need ${fmtMoney(s.credit_needed)} cash/credit` : "No credit needed"}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      <ScenarioBar current={buildBody(p)} onSwitched={remount} />
      <WhatIfPlanner
        items={p.plan_items || []}
        setItems={(items) => setP((x) => ({ ...x, plan_items: items }))}
        plan={fc.plan}
        firstMonth={chart[2]?.month || chart[0]?.month || ""}
      />
    </Card>
  );
}
